import { toast } from 'react-toastify';
import statusModal from '@pages/Forms/components/modals/statusModal';
import SubmissionModal from '@pages/Forms/components/modals/SubmissionModal';
import { useApp, useJpa, useModal, useRequest } from '@stores';
import { LoadingMessages, RequestStatus, RequestType, ResponseStatuses, StorageScopes } from '@src/utils/enums';
import { queryClient } from '@src/api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '@src/api/services/dashboard';
import { PROJECT_QUERY_KEYS } from '@src/api/services/project';
import { router } from '@src/Router';

const handleSuccess = (signed, body, store, dispatch, submissionModal) => {
  const { setIsSavingProgress, setIsLoading } = useApp.getState();
  const { openModal } = useModal.getState();

  setIsSavingProgress(false);
  setIsLoading(false);

  if (signed) {
    toast.success(`Pre-application has been successfully submitted!`);
    openModal(submissionModal, {
      msg: body?.msg,
      status: body?.status ?? ResponseStatuses.Success,
    });
    router.navigate({ to: '/dashboard' });
  } else {
    toast.success('Pre-application has been successfully saved!');
    dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: body.data });
    dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: body.data });
    store.doUpdatePreAppRequest(body.data.request.preApplications[0]);
    dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { requestStatus: RequestStatus.Draft } });
  }
};

const preAppFormDataBundle = {
  name: 'preAppFormData',

  getReducer: () => {
    const initialState = {
      preApplications: [],
      errors: {},
    };

    return (state = initialState, { type, payload }) => {
      switch (type) {
        case 'RESET_PRE_APP':
          return initialState;
        case 'UPDATE_PRE_APP':
          return {
            ...state,
            preApplications: [{ ...state.preApplications[0], ...payload }],
          };
        case 'UPDATE_PRE_APP_ERRORS':
          return { ...state, errors: payload };
        default:
          return state;
      }
    };
  },

  selectPreAppData: (state) => state.preAppFormData.preApplications[0],
  selectPreAppID: (state) => state.preAppFormData.preApplications[0]?.preAppID,
  selectPreAppErrors: (state) => state.preAppFormData.errors,

  doFetchPreApp:
    (projectID, requestID, requestStatus, isGenerate = false, archived) =>
    ({ apiGet, dispatch, store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();
      const { setSelectedRequestId } = useRequest.getState();

      !isGenerate && setIsLoading(true, 'Retrieving Pre-application Request Information...');
      store.doResetPreAppRequest();
      store.doResetRequestData();

      const uri =
        '/api/PreApplication/getPreApplication?' +
        new URLSearchParams({
          projectID,
          requestID,
        });

      return apiGet(uri, (err, body) => {
        !isGenerate && setIsLoading(false);
        if (!err && body.status === ResponseStatuses.Success) {
          const responseData = { ...body?.data, archived };
          const requestData = responseData?.request;
          const jpaBasicInfo = requestData?.jpaBasicInfo;
          const preAppData = requestData?.preApplications;
          const preAppID = requestData?.preApplications?.[0]?.preAppID;

          dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: responseData });
          dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: responseData });
          dispatch({
            type: 'UPDATE_REQUEST_OBJECT',
            payload: { requestStatus: requestStatus },
          });

          useJpa.setState({ jpaBasicInfo });

          dispatch({
            type: 'UPDATE_PRE_APP',
            payload: preAppData?.filter((item) => item.preAppID === preAppID)[0],
          });
          if (isGenerate) {
            store.doGenerateDraftDocuments();
          } else {
            queryClient.invalidateQueries({
              queryKey: [
                PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
                store.selectProjectID(),
                store.selectRequestID(),
                store.selectVersion(),
              ],
            });
            setSelectedRequestId('5');
            router.navigate({
              to: '/forms/$id',
              params: { id: String(requestID) },
            });
            store.doFetchUserFilesDetails({ componentID: preAppID, scope: StorageScopes.ProjectFolder }).finally(() => {
              dispatch({ type: 'SET_LOADING_STATE', payload: false });
            });
          }
        } else {
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while retrieving the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Retrieval Error',
          });
          dispatch({ type: 'SET_LOADING_STATE', payload: false });
        }
      });
    },
  doAddPreApp:
    (data) =>
    ({ apiPost, dispatch, store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal, closeModal } = useModal.getState();
      const { resetToDefaults } = useJpa.getState();

      setIsLoading(true, 'Creating Pre-application...');
      resetToDefaults();

      const uri = '/api/PreApplication/addPreApplication';

      return apiPost(uri, data, (err, body) => {
        setIsLoading(false);
        if (!err && body.status === ResponseStatuses.Success) {
          dispatch({
            type: 'UPDATE_REQUEST_API_DATA',
            payload: {
              projectID: body.data.projectID,
              projectVersion: body.data.projectVersion,
              request: { requestID: body.data.requestID, permission: body.data.permission },
            },
          });
          dispatch({
            type: 'UPDATE_REQUEST_FORM_DATA',
            payload: {
              projectID: body.data.projectID,
              projectVersion: body.data.projectVersion,
              request: { requestID: body.data.requestID },
            },
          });

          useJpa.setState({ jpaBasicInfo: body.data.jpaBasicInfo });

          dispatch({
            type: 'UPDATE_REQUEST_OBJECT',
            payload: {
              requestStatus: RequestStatus.Draft,
              requestType: RequestType.PreApplication,
              requestID: body.data.requestID,
            },
          });
          dispatch({
            type: 'UPDATE_PRE_APP',
            payload: { preAppID: body.data.preAppID },
          });
          router.navigate({ to: '/forms' });
          store.doFetchUserFilesDetails({ componentID: body.data.preAppID, scope: StorageScopes.ProjectFolder });
          closeModal();
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
          queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEYS.GET_PROJECT_LOCATION] });
          queryClient.invalidateQueries({
            queryKey: [
              PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
              store.selectProjectID(),
              store.selectRequestID(),
              store.selectVersion(),
            ],
          });
        } else {
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while creating the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Creation Error',
          });
        }
      });
    },
  doUploadAndUpdatePreApp:
    (data, status) =>
    ({ store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();

      const signed = Boolean(data?.request?.isSubmit);
      setIsSavingProgress(true);
      signed && setIsLoading(true, LoadingMessages.SubmitRequest);

      if (store.selectValidFiles()?.length > 0) {
        // Format to FormData
        const formdata = new FormData();
        formdata.append('overwrite', false);
        formdata.append('isAnonymous', false);
        store.selectValidFiles().forEach((item) => {
          const fileMetaData = {
            ProjectID: store.selectProjectID(),
            RequestID: store.selectRequestID(),
            Version: 0,
            Type: status,
            Tags: item?.tags,
          };
          formdata.append('files', item?.file);
          formdata.append('fileMetaData', JSON.stringify(fileMetaData));
        });

        Promise.all([store.doUploadFiles(formdata)])
          .then((results) => {
            if (results?.[0]?.status === ResponseStatuses.Success) {
              // if all files upload successfully
              store.doUpdatePreApp(data);
              store.doResetUsersFileData();
              store.doFetchUserFilesDetails({
                componentID: store.selectPreAppID(),
                scope: StorageScopes.ProjectFolder,
              });
            } else {
              // if any of the files failed to upload
              setIsSavingProgress(false);
              store.doResetUsersFileData();
              openSecondaryModal(statusModal, {
                status: 'ERROR! Failed Uploads',
                msg: (
                  <>
                    <p>{results?.[0]?.msg}</p>
                    <p>Retry saving/submitting your Pre-application Request in a few minutes.</p>
                  </>
                ),
                saveText: 'Return to request',
              });
            }
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        // If no files were selected, just update PreApp
        store.doUpdatePreApp(data);
      }
    },
  doUpdatePreApp:
    (data) =>
    ({ apiPut, dispatch, store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();

      const signed = Boolean(data.request.isSubmit);
      setIsSavingProgress(true);
      setIsLoading(true, signed ? LoadingMessages.SubmitRequest : LoadingMessages.SaveRequest);

      const uri = '/api/PreApplication/updatePreApplication';

      return apiPut(uri, data, (err, body) => {
        if (
          !err &&
          [ResponseStatuses.Success, ResponseStatuses.SuccessWithWarning, ResponseStatuses.Warning].includes(
            body?.status
          )
        ) {
          handleSuccess(signed, body, store, dispatch, SubmissionModal);
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
          queryClient.invalidateQueries({
            queryKey: [
              PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
              store.selectProjectID(),
              store.selectRequestID(),
              store.selectVersion(),
            ],
          });
          queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEYS.GET_PROJECT_LOCATION] });
        } else {
          setIsSavingProgress(false);
          setIsLoading(false);
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while saving or submitting the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Save/Submission Error',
          });
          dispatch({ type: 'UPDATE_PRE_APP_ERRORS', payload: body?.data });
        }
      });
    },
  doUpdatePreAppRequest:
    (data) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'UPDATE_PRE_APP', payload: data });
      dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { preApplications: [store.selectPreAppData()] } });
    },
  doResetPreAppRequest:
    () =>
    ({ dispatch }) => {
      dispatch({ type: 'RESET_PRE_APP' });
    },
};

export default preAppFormDataBundle;
